import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Grid, Typography, Tooltip } from '@mui/material';
import { DataGrid, Modal, SearchInput } from '../ui';
import { IGridcolumn } from '../ui/interface/column.interface';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { toggleAlert } from '../store/resuders/app.reducer';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { formatTimestamp } from '../helpers/date.helper';
import { deleteIcon, undoIcon } from '../assets';
import {
  getDeletedTestCaseList,
  restoreTestCase,
  bulkRestoreTestCases,
  purgeTestCase,
  bulkPurgeTestCases,
} from '../services/testcase.service';

type ConfirmAction = 'restore' | 'purge';

export const RecycleBin = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [data, setData] = useState<any>({ content: [], totalPages: 0 });
  const [remove, setRemove] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>('restore');
  const [criteria, setCriteria] = useState<any>({
    sort: '-deletedAt',
    pageNo: 0,
    pageSize: 20,
    filter: localStorage.getItem('recycleBinFilter') || '',
  });

  const columns = useMemo(() => {
    const recycleColumns: IGridcolumn[] = [
      {
        fieldName: 'testCaseId',
        caption: '',
        sortable: false,
        width: '1px',
        key: true,
        redner: () => <div style={{ width: '1px', height: '1px', overflow: 'hidden' }}></div>,
      },
      {
        fieldName: 'projectId',
        caption: t('Project ID'),
        sortable: true,
        width: '10%',
      },
      {
        fieldName: 'testCaseId',
        caption: t('ID'),
        sortable: true,
        width: '10%',
      },
      {
        fieldName: 'name',
        caption: t('Test Case Name'),
        sortable: true,
        width: 'auto',
      },
      {
        fieldName: 'deletedAt',
        caption: t('Deleted'),
        sortable: true,
        width: '20%',
        redner: (props: any) => <div style={{ whiteSpace: 'nowrap' }}>{formatTimestamp(props.deletedAt)}</div>,
      },
      {
        fieldName: 'deletedBy',
        caption: t('Deleted By'),
        sortable: true,
        width: '20%',
      },
    ];
    return recycleColumns;
  }, [t]);

  const loadRecycleBinData = async () => {
    const response = await getDeletedTestCaseList(
      criteria.pageNo,
      criteria.pageSize,
      criteria.sort,
      criteria.filter,
    );
    setData(response?.data || { content: [], totalPages: 0 });
  };

  useEffect(() => {
    loadRecycleBinData();
  }, [criteria]);

  const sortChanged = (column: string, direction: string) => {
    setCriteria({
      ...criteria,
      pageNo: 0,
      sort: `${direction === 'ASC' ? '+' : '-'}${column}`,
    });
  };

  const pageChanged = (page: number) => {
    setCriteria({
      ...criteria,
      pageNo: page - 1,
    });
  };

  const pageSizeChanged = (size: number) => {
    setCriteria({
      ...criteria,
      pageNo: 0,
      pageSize: size,
    });
  };

  const onFilterChanged = (filter: string) => {
    setCriteria({
      ...criteria,
      pageNo: 0,
      filter,
    });
    localStorage.setItem('recycleBinFilter', filter);
  };

  const selectionChanged = (ids: any) => {
    setRemove(ids || []);
  };

  const groupedSelectionByProject = () => {
    const selectedRows = (data?.content || []).filter((row: any) => remove.includes(row.testCaseId));
    const grouped: Record<number, number[]> = {};
    selectedRows.forEach((row: any) => {
      const pid = Number(row.projectId);
      if (!grouped[pid]) grouped[pid] = [];
      grouped[pid].push(Number(row.testCaseId));
    });
    return grouped;
  };

  const restoreSelected = () => {
    if (!remove?.length) return;
    setConfirmAction('restore');
    setIsOpen(`${Date.now()}`);
  };

  const purgeSelected = () => {
    if (!remove?.length) return;
    setConfirmAction('purge');
    setIsOpen(`${Date.now()}`);
  };

  const restoreCases = async () => {
    const grouped = groupedSelectionByProject();
    let hasError = false;

    for (const [pidRaw, ids] of Object.entries(grouped)) {
      const pid = Number(pidRaw);
      const result =
        ids.length === 1
          ? await restoreTestCase(pid, ids[0])
          : await bulkRestoreTestCases(pid, ids);
      if (['ERR_BAD_RESPONSE', 'ERR_BAD_REQUEST'].includes(result?.code)) {
        hasError = true;
        dispatch(toggleAlert(result?.response?.data?.data?.messages?.join(',')));
        break;
      }
    }

    if (!hasError) {
      dispatch(toggleAlert(t('Test Case Restored Successfully')));
      setRemove([]);
      await loadRecycleBinData();
    }
  };

  const purgeCases = async () => {
    const grouped = groupedSelectionByProject();
    let hasError = false;

    for (const [pidRaw, ids] of Object.entries(grouped)) {
      const pid = Number(pidRaw);
      const result =
        ids.length === 1
          ? await purgeTestCase(pid, ids[0])
          : await bulkPurgeTestCases(pid, ids);
      if (['ERR_BAD_RESPONSE', 'ERR_BAD_REQUEST'].includes(result?.code)) {
        hasError = true;
        dispatch(toggleAlert(result?.response?.data?.data?.messages?.join(',')));
        break;
      }
    }

    if (!hasError) {
      dispatch(toggleAlert(t('Test Case Purged Successfully')));
      setRemove([]);
      await loadRecycleBinData();
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Grid container>
        <Grid item xs={12} paddingTop={'16px'} paddingBottom={'10px'}>
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '12px' }}>
            <Typography variant="subtitle2">{t('Recycle Bin')}</Typography>
          </div>
        </Grid>

        <Grid item xs={12} paddingRight={'9px'}>
          <Card>
            <CardContent>
              <div className="test-result-header">
                <div className="action-header">
                  <Tooltip title={t('Restore')} arrow>
                    <img
                      src={undoIcon}
                      alt={t('Restore')}
                      onClick={restoreSelected}
                      style={{ opacity: remove?.length ? 1 : 0.45, cursor: remove?.length ? 'pointer' : 'not-allowed' }}
                    />
                  </Tooltip>
                  <Tooltip title={t('Purge')} arrow>
                    <img
                      src={deleteIcon}
                      alt={t('Purge')}
                      onClick={purgeSelected}
                      style={{ opacity: remove?.length ? 1 : 0.45, cursor: remove?.length ? 'pointer' : 'not-allowed' }}
                    />
                  </Tooltip>
                </div>
                <div className="test-results-filters">
                  <SearchInput onFilter={onFilterChanged} defaultValue={criteria.filter} />
                </div>
              </div>

              <DataGrid
                pageChanged={pageChanged}
                totalPages={data?.totalPages || 0}
                sortChanged={sortChanged}
                columns={columns}
                data={data?.content || []}
                selection={true}
                sortBy={criteria.sort}
                selectionChanged={selectionChanged}
                pageSizeChanged={pageSizeChanged}
              />
            </CardContent>
          </Card>
        </Grid>

        <Modal
          isOpen={isOpen}
          title={confirmAction === 'restore' ? t('Restore Items') : t('Purge Items')}
          small={true}
          deleteConfirmation={true}
          ButtonDelete={confirmAction === 'restore' ? restoreCases : purgeCases}
        />
      </Grid>
    </DndProvider>
  );
};
